import { Grid, Typography } from "@mui/material";
import { post as apiPost } from "../../../actions/api";
import { Cache } from "aws-amplify/utils";
import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactSelect from "react-select";
import { SnackbarProvider } from 'notistack';
import {
  inviteToVacancy
} from "../../../actions/candidates_actions";
import config from "../../../config";
import JobListingView  from "./JobListingView";
import { JobRecommendationsTable } from "./JobRecommendationsTable";
import {EntitiesAutoComplete} from "../../Common/EntitiesAutoComplete"
import _ from "lodash";

const formatToSterling = (amount) =>
  new Intl.NumberFormat("en-gb", {
    style: "currency",
    currency: config.currency,
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  }).format(amount);

const createSalaryOptions = (salaryInterests) => {
  const availableSalaryOptions = [0, 15000, 20000, 25000, 30000, 50000]
  return availableSalaryOptions
    .map(formatToSterling)
    .map((salary) => ({ label: (salary === "£0" || salary === "€0" ? "No preference" : salary), value: salary }))

};

const contractTypeOptions = ["Full Time", "Part Time", "Either"].map((contract_type) => ({
  label: contract_type,
  value: contract_type,
}));

const JobResults = (props) => {
  const { candidate: candidateProp, job_match_type: jobMatchTypeProp, set_job_match_type } = props;
  const dispatch = useDispatch();

  const {
    selectedCandidateData: selectedCandidate = {},
    desired_occupations,
    interest_occupations,
    past_occupations,
    skills_backpack_occupations,
    styles_occupations
  } = useSelector((state) => state.candidates);

  const getInitialState = () => {
    const { candidate: cachedCandidate, job_match_type: cachedJobMatchType } =
      Cache.getItem("currentSkillzminer") || {};
    return {
      candidate: candidateProp || cachedCandidate || null,
      jobRecommendations: null,
      isPending: false,
      selectedJob: null,
      job_match_type: cachedJobMatchType || jobMatchTypeProp || "previous",
      salary_range: config.distanceName === "miles" ? "£0" : "€0",
      contract_type: "Either",
      travel_distance: config.distanceName === "miles" ? "50" : "75",
    };
  };

  const [candidate, setCandidate] = useState(getInitialState().candidate);
  const [jobRecommendations, setJobRecommendations] = useState(getInitialState().jobRecommendations);
  const [isPending, setIsPending] = useState(getInitialState().isPending);
  const [selectedJob, setSelectedJob] = useState(getInitialState().selectedJob);
  const [jobMatchType, setJobMatchType] = useState(getInitialState().job_match_type);
  const [salaryRange, setSalaryRange] = useState(getInitialState().salary_range);
  const [contractType, setContractType] = useState(getInitialState().contract_type);
  const [travelDistance, setTravelDistance] = useState(getInitialState().travel_distance);

  const onJobMatchTypeChange = (value) => {
    const lowerValue = value.toLowerCase();
    if (lowerValue !== jobMatchType) {
      setJobMatchType(lowerValue);
    }
  };

  const selectOnChangeHandler = (field) => ({ value }) => {
    if (field === 'salary_range' && value !== salaryRange) setSalaryRange(value);
    if (field === 'contract_type' && value !== contractType) setContractType(value);
    if (field === 'travel_distance' && value !== travelDistance) setTravelDistance(value);
  };

  useEffect(() => {
    if (jobMatchType) {
      fetchJobRecommendations();
    }
  }, [jobMatchType, salaryRange, contractType, travelDistance, selectedCandidate]);

  useEffect(() => {
    if (jobMatchTypeProp && jobMatchType !== jobMatchTypeProp) {
      setJobMatchType(jobMatchTypeProp);
      set_job_match_type(null);
    }
  }, [jobMatchTypeProp]);

  const fetchJobRecommendations = async () => {
    setIsPending(true);

    // Helper function from original component
    const returnMappedResults = (occ1, occ2, candidate) => {
      let mappedResults = _.map(occ1, (index) => {
        let f = _.filter(occ2, (occ) => {
          if (occ.occupation === index.occupation) {
            return occ;
          }
          return null;
        });

        if (f && f.length > 0) {
          f = f[0];
        }

        if (f && f.ONET_styles) {
          _.each(f.ONET_styles, (style, key) => {
            switch (key) {
              case "Commitment":
                f.COMM = style;
                break;
              case "Adaptability":
                f.ADY = style;
                break;
              case "Confidence":
                f.CONF = style;
                break;
              case "Control":
                f.CONT = style;
                break;
              case "Accuracy":
                f.PSA = style;
                break;
              case "Responsiveness to feedback":
                f.RTF = style;
                break;
              case "Complexity":
                f.SPC = style;
                break;
              default:
                break;
            }
            });
          }
        if (f && f.ONET_interests) {
          _.each(f.ONET_interests, (interest, key) => {
            f[key.toLowerCase()] = interest;
          });
        }

        if (candidate.qualifications && candidate.qualifications.category) {
          if (f.ONET_required_level_education > candidate.qualifications.category) {
            f.score_quals = false;
          } else {
            f.score_quals = true;
          }
        } else {
          f.score_quals = false;
        }
        let results = {
          ...f,
          ...index
        };
        return results;
      });
      return _.orderBy(mappedResults, [({ provider }) => provider === 'skillzMiner'], ["desc"]);
    }

    const {
      provider,
      latestAssessmentResult,
      occupations,
      desiredOccupations,
      desiredOccupationsExtended,
      qualifications,
      desiredExp,
      occupationsExtended,
      skills_names,
      occupationsOriginal,
      desiredOccupationsOriginal,
      userID
    } = _.cloneDeep(selectedCandidate);

    if (!latestAssessmentResult) {
      setJobRecommendations([]);
      setIsPending(false);
      return;
    }
    if (salaryRange) {
      latestAssessmentResult.interests.salary = [].concat(salaryRange);
    }
    if (contractType) {
      latestAssessmentResult.interests.contract = [].concat(contractType);
    }
    if (travelDistance) {
      latestAssessmentResult.interests.locationRadius = [].concat(travelDistance);
    }

    try {
      switch (jobMatchType) {
        case "previous": {
          const payload = {
            provider,
            latestAssessmentResult,
            occupationsExtended,
            occupations,
            desiredExp,
            "desired_occupations": [],
            "styles_occupations" : [],
            "interest_occupations" : [],
            "skills_backpack_occupations" : [],
            qualifications,
            skills_names,
            past_occupations
          };
          if (desiredExp) {
            Object.assign(payload, { desiredExp, desiredOccupations, desiredOccupationsExtended });
          }

          const { jobs } = await apiPost("/jobs/query/all_jobs", {
            data: payload,
          });

          jobs.past_occupations = returnMappedResults(jobs.past_occupations, past_occupations, selectedCandidate);
          setJobRecommendations(jobs.past_occupations);
          break;
        }
        case "preferred": {
          const payload = {
            provider,
            latestAssessmentResult,
            occupationsExtended,
            occupations,
            desiredExp,
            desired_occupations,
            "styles_occupations" : [],
            "interest_occupations" : [],
            "skills_backpack_occupations" : [],
            qualifications,
            skills_names,
            "past_occupations": []
          };
          if (desiredExp) {
            Object.assign(payload, { desiredExp, desiredOccupations, desiredOccupationsExtended });
          }

          const { jobs } = await apiPost("/jobs/query/all_jobs", {
            data: payload,
          });

          jobs.desired_occupations = returnMappedResults(jobs.desired_occupations, desired_occupations,selectedCandidate);
          setJobRecommendations(jobs.desired_occupations);
          break;
        }
        case "skills": {
          const payload = {
            provider,
            latestAssessmentResult,
            occupationsExtended,
            occupations,
            desiredExp,
            "desired_occupations": [],
            "styles_occupations" : [],
            "interest_occupations" : [],
            skills_backpack_occupations,
            qualifications,
            skills_names,
            "past_occupations": []
          };
          if (desiredExp) {
            Object.assign(payload, { desiredExp, desiredOccupations, desiredOccupationsExtended });
          }

          const { jobs } = await apiPost("/jobs/query/all_jobs", {
            data: payload,
          });

          jobs.skills_backpack_occupations = returnMappedResults(jobs.skills_backpack_occupations, skills_backpack_occupations, selectedCandidate);
          setJobRecommendations(jobs.skills_backpack_occupations);
          break;
        }
        case "styles": {
          const payload = {
            provider,
            latestAssessmentResult,
            occupationsExtended,
            occupations,
            desiredExp,
            "desired_occupations": [],
            styles_occupations,
            "interest_occupations" : [],
            "skills_backpack_occupations": [],
            qualifications,
            skills_names,
            "past_occupations": []
          };
          if (desiredExp) {
            Object.assign(payload, { desiredExp, desiredOccupations, desiredOccupationsExtended });
          }

          const { jobs } = await apiPost("/jobs/query/all_jobs", {
            data: payload,
          });

          jobs.styles_occupations = returnMappedResults(jobs.styles_occupations, styles_occupations, selectedCandidate);
          setJobRecommendations(jobs.styles_occupations);

          break;
        }
        case "interests": {
          const payload = {
            provider,
            latestAssessmentResult,
            occupationsExtended,
            occupations,
            "desired_occupations": [],
            "styles_occupations": [],
            interest_occupations,
            "skills_backpack_occupations": [],
            qualifications,
            skills_names,
            "past_occupations": []
          };


          const { jobs } = await apiPost("/jobs/query/all_jobs", {
            data: payload,
          });

          jobs.interest_occupations = returnMappedResults(jobs.interest_occupations, interest_occupations, selectedCandidate);
          setJobRecommendations(jobs.interest_occupations);
          break;
        }
        case "jobsnearme": {
          const payload = {
            provider,
            latestAssessmentResult,
            occupations,
            occupationsExtended,
            desiredExp,
            qualifications,
            desiredOccupations,
            calculateQualifications: false,
            skills_names
          };
      
          if (payload.latestAssessmentResult.interests){
            payload.latestAssessmentResult.interests.salary = ["0"];
          }
      
          if (payload.latestAssessmentResult.interests){
            payload.latestAssessmentResult.interests.contract = ["Either"];
          }


          const { jobs } = await apiPost("/jobs/query/jobs_near_me", {
            data: payload,
          });
          setJobRecommendations(_.orderBy(jobs, [({ provider }) => provider === 'skillzMiner'], ["desc"]));
          break;
        }
        default: {
          let matchedOcc = false
          for (const value of [desired_occupations, interest_occupations, past_occupations, skills_backpack_occupations, styles_occupations]){
            for (const occ of value){
              if (occ.occupation === jobMatchType){
                matchedOcc = occ
                break
              }
            } 
            if (matchedOcc){
              break
            }
          }

          if (!matchedOcc){
            let occPayload = {
              latestAssessmentResult,
              desiredOccupations,
              occupationsExtended,
              occupations,
              desiredExp,
              qualifications,
              calculateQualifications: false,
              skills_names,
              userID,
              occupationsOriginal,
              desiredOccupationsOriginal,
            };
            for (const idx in occPayload.desiredExp) {
              if (occPayload.desiredExp[idx] === true){
                occPayload.desiredExp[idx] = false
                occPayload.occupations.push(occPayload.desiredOccupations[idx])
                occPayload.occupationsExtended.push(desiredOccupationsExtended[idx])
                occPayload.occupationsOriginal.push(desiredOccupationsOriginal[idx])
              }
            }
            occPayload.desiredOccupations = [jobMatchType]
            occPayload.desiredOccupationsOriginal = [jobMatchType]
            const new_jobs_cats = await apiPost(
              
              "/jobs/query/all_occupations",
              { data: occPayload }
            );
            if(new_jobs_cats && new_jobs_cats.jobs){
              for (const occ of new_jobs_cats.jobs.desired_occupations){
                if (occ.occupation === jobMatchType){
                  matchedOcc = occ
                  break
                }
              }
            }
          }

          if (matchedOcc){
            const payload = {
              provider,
              latestAssessmentResult,
              occupationsExtended,
              occupations,
              "desiredExp": [false],
             "desired_occupations": [matchedOcc],
              "styles_occupations" : [],
              "interest_occupations" : [],
              "skills_backpack_occupations" : [],
              qualifications,
              skills_names,
              "past_occupations": []
            };
            const { jobs } = await apiPost("/jobs/query/all_jobs", {
              data: payload,
            });
            jobs.desired_occupations = returnMappedResults(jobs.desired_occupations, [matchedOcc], selectedCandidate);
            setJobRecommendations(_.orderBy(jobs.desired_occupations, [({ provider }) => provider === 'skillzMiner'], ["desc"]));
            break;
          }          
        }
      }
    } catch (error) {
      setIsPending(false);
      setJobRecommendations([]);
    }
    setIsPending(false);
    return;
  };

  const doInviteToVacancy = (userID, vacancy) => {
    dispatch(inviteToVacancy(userID, vacancy));
  };

    const candidateHasLatestAssessment =
      selectedCandidate && !!selectedCandidate.latestAssessmentResult;
    if (!candidateHasLatestAssessment) {
      return (
        <Grid>
          <Grid container item xs justify="center">
            <Typography variant="subtitle2">
              This candidate has not completed the assessment yet.
            </Typography>
          </Grid>
        </Grid>
      );
    }
    const {
      latestAssessmentResult: {
        interests: { salary = [], contract = [], locationRadius = [] } = {},
      } = {},
    } = selectedCandidate;

    const salaryRangeOptions = createSalaryOptions(
      salary.map((salary) => Number(salary.replace(/[^0-9.]+/g, "")))
    );

    const locationRadiusOptions = Array.from(
      new Set(config.distanceName === "miles" ? [10, 20, 30, 50, 500] : [15, 20, 30, 50, 75, 500]))
      .map((radius) => ({
        value: radius.toString(),
        label: (radius === 500 ? "No limit" : `up to ${radius} ${config.distanceName}`),
      }))


    const makeToolBar = () => {
      return (<div style={{marginTop:15}}>
        <Grid item size>
          <Grid
            container
            direction="row"
            alignItems="center"
            spacing={5}
            alignContent="center"
            style={{ paddingBottom: "1em" }}
          >
            <Grid item size={{xs:4}} style={{color:"black"}}>
              <EntitiesAutoComplete
                size={"large"}
                onSelect={onJobMatchTypeChange}
                placeholder={"Previous"}
                useJobMatchOption={true}
                disabled={
                  isPending ||
                  !candidateHasLatestAssessment ||
                  !candidate
                }
              />
            </Grid>
            <Grid item size={{xs:2}}>
              <ReactSelect
                styles={{ container: (base) => ({ ...base, zIndex: 999 }) }}
                value={
                  salaryRange
                    ? salaryRangeOptions.find(
                        ({ value }) => value === salaryRange
                      )
                    : salaryRangeOptions.find(
                        ({ value }) => value === salary[0]
                      ) || salaryRangeOptions[0]
                }
                onChange={selectOnChangeHandler("salary_range")}
                placeholder="Salary Range"
                isSearchable
                options={salaryRangeOptions}
                isDisabled={
                  isPending ||
                  !candidateHasLatestAssessment ||
                  !candidate ||
                  jobMatchType === "jobsnearme"
                }
              />
            </Grid>
            <Grid item size={{xs:2}}>
              <ReactSelect
                styles={{ container: (base) => ({ ...base, zIndex: 999 }) }}
                value={
                  contractType
                    ? contractTypeOptions.find(
                        ({ value }) => value === contractType
                      )
                    : contractTypeOptions.find(
                        ({ value }) => value === contract[0]
                      ) || contractTypeOptions[0]
                }
                onChange={selectOnChangeHandler("contract_type")}
                placeholder="Contract Type"
                isSearchable
                options={contractTypeOptions}
                isDisabled={
                  isPending ||
                  !candidateHasLatestAssessment ||
                  !candidate ||
                  jobMatchType === "jobsnearme"
                }
              />
            </Grid>
            <Grid item size={{xs:2}}>
              <ReactSelect
                styles={{ container: (base) => ({ ...base, zIndex: 999 }) }}
                value={
                  travelDistance
                    ? locationRadiusOptions.find(
                        ({ value }) => value === travelDistance
                      )
                    : locationRadiusOptions.find(
                        ({ value }) => value === locationRadius[0]
                      ) || locationRadiusOptions[0]
                }
                onChange={selectOnChangeHandler("travel_distance")}
                placeholder="Maximum Travel Distance"
                isSearchable
                options={locationRadiusOptions}
                isDisabled={
                  isPending ||
                  !candidateHasLatestAssessment ||
                  !candidate ||
                  jobMatchType === "jobsnearme"
                }
              />
            </Grid>
          </Grid>
        </Grid>
        </div>
    )}
    
    return (
      <Grid container direction="column">
        <Grid item size>
        <div>
          {selectedJob && (
            <SnackbarProvider>
              <JobListingView
                onDismiss={() => setSelectedJob(null)}
                job={selectedJob}
                candidate={selectedCandidate}
                onInviteToVacancy={(userID, vacancy) => {
                  doInviteToVacancy(userID, vacancy)
                }}
              />
            </SnackbarProvider>
          )}
          </div>
            {makeToolBar()}
            <JobRecommendationsTable
              jobMatchType={jobMatchType}
              jobRecommendations={(jobRecommendations || []).filter(
                (job) => {
                  return true;
                }
              )}
              isLoading={isPending}
              onRowClick={(_, data_) => {
                setSelectedJob(data_);
              }}
              optionalMessageWhenEmpty={
                selectedCandidate &&
                !selectedCandidate.latestAssessmentResult
                  ? "This candidate has not completed the assessment yet."
                  : undefined
              }
            />
        </Grid>
      </Grid>
    );
  }

export default JobResults;
